import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Search, X, CheckCircle2, AlertTriangle, Loader2, Pencil } from 'lucide-react';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OfficialExercisePicker
 * Buscador inline para vincular un ejercicio del plan con la librería oficial v2.
 *
 * Props:
 *  - currentName: nombre actual del ejercicio en el plan
 *  - currentExerciseId: GF-XXX si ya está vinculado, null si no
 *  - onPick({ exercise_id, nombre_oficial, video_url, video_filename }): callback al elegir
 *  - compact: boolean (default false) — versión más pequeña
 *
 * Search:
 *  - busca por nombre Y por exercise_id (GF-XXX)
 *  - filtra solo estado=OFICIAL
 */
const OfficialExercisePicker = ({ currentName, currentExerciseId, onPick, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [allOfficial, setAllOfficial] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Cargar 202 oficiales al abrir
  useEffect(() => {
    if (!open || allOfficial.length > 0) return;
    setLoading(true);
    axios.get(`${API}/exercises`)
      .then(r => {
        const oficiales = (r.data || []).filter(e =>
          (e.exercise_id || '').startsWith('GF-') &&
          (e.estado || '').toUpperCase() === 'OFICIAL'
        );
        setAllOfficial(oficiales);
      })
      .catch(() => setAllOfficial([]))
      .finally(() => setLoading(false));
  }, [open, allOfficial.length]);

  // Auto-focus al abrir
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Cerrar con click fuera o ESC
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allOfficial;
    return allOfficial.filter(e =>
      (e.nombre_oficial || e.name || '').toLowerCase().includes(q) ||
      (e.exercise_id || '').toLowerCase().includes(q) ||
      (e.categoria || '').toLowerCase().includes(q)
    );
  }, [query, allOfficial]);

  const handlePick = (ex) => {
    onPick({
      exercise_id: ex.exercise_id,
      nombre_oficial: ex.nombre_oficial || ex.name,
      video_url: ex.video_url || '',
      video_filename: ex.video_filename || `${ex.exercise_id}.mp4`,
    });
    setOpen(false);
    setQuery('');
  };

  const isLinked = !!currentExerciseId && currentExerciseId.startsWith('GF-');

  return (
    <div ref={containerRef} className="relative inline-flex items-center gap-2" data-testid="official-exercise-picker">
      {/* Botón principal: muestra estado actual + abre buscador */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 ${compact ? 'text-xs' : 'text-sm'} px-2 py-1 rounded-md transition-colors
          ${isLinked
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20'
            : 'bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
          }`}
        data-testid="picker-trigger"
        title={isLinked ? `Vinculado a ${currentExerciseId}. Click para cambiar.` : 'Sin vincular. Click para vincular a librería oficial.'}
      >
        {isLinked ? (
          <>
            <CheckCircle2 className="h-3 w-3" />
            <span className="font-mono">{currentExerciseId}</span>
          </>
        ) : (
          <>
            <AlertTriangle className="h-3 w-3" />
            <span>Vincular</span>
          </>
        )}
        <Pencil className="h-3 w-3 opacity-50" />
      </button>

      {/* Dropdown del buscador */}
      {open && (
        <div
          className="absolute z-50 top-full mt-1 left-0 w-[420px] max-w-[90vw] bg-zinc-950 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden"
          data-testid="picker-dropdown"
        >
          <div className="p-2 border-b border-zinc-800">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre o GF-XXX..."
                className="w-full bg-zinc-900 border border-zinc-700 rounded-md h-9 pl-8 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/50"
                data-testid="picker-search"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-2.5"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {loading ? 'Cargando…' : `${filtered.length} de ${allOfficial.length} oficiales`}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading && (
              <div className="p-4 text-center text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Cargando librería oficial…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-4 text-center text-muted-foreground text-sm">
                Sin resultados
              </div>
            )}
            {!loading && filtered.map((ex) => (
              <button
                key={ex.exercise_id}
                type="button"
                onClick={() => handlePick(ex)}
                className="w-full text-left p-2 hover:bg-zinc-900 border-b border-zinc-900 last:border-b-0 transition-colors"
                data-testid={`picker-item-${ex.exercise_id}`}
              >
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="font-mono text-[10px] border-amber-700/40 text-amber-400 flex-shrink-0 mt-0.5">
                    {ex.exercise_id}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground truncate">{ex.nombre_oficial || ex.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {ex.categoria || '—'}
                      {ex.video_url && <span className="text-emerald-400 ml-2">● vídeo</span>}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="p-1.5 border-t border-zinc-800 bg-zinc-900/50 text-[10px] text-muted-foreground text-center">
            Solo se muestran ejercicios con <code className="px-1 bg-zinc-800 rounded">estado=OFICIAL</code> · ESC para cerrar
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficialExercisePicker;
